import { Button, FormControlLabel, Grid, Switch, TextField } from '@material-ui/core';
import React, { useCallback, useMemo, useState } from 'react';
import { Country, countryNames, idTemplateTypes } from './constants';
import { ReusableSelect as Select } from 'components/reusable/select';
import { setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { adminCreateTemplateFromBase, adminLoadTemplate, adminUpdateTemplateConfiguration } from 'store/sagas/sagas';
export const TemplateCreateFromBaseForm = () => {
    const { asyncDispatch, dispatch } = useGlobalState();
    const [baseTemplateId, setBaseTemplateId] = useState('');
    const [baseTemplate, setBaseTemplate] = useState();
    const [baseTemplateInactive, setBaseTemplateInactive] = useState(true);
    const [baseTemplateType, setBaseTemplateType] = useState('');
    const [baseTemplateCountry, setBaseTemplateCountry] = useState('');
    const [baseTemplateState, setBaseTemplateState] = useState('');
    const [baseTemplateVersion, setBaseTemplateVersion] = useState('');
    const [newTemplateType, setNewTemplateType] = useState();
    const [newTemplateCountry, setNewTemplateCountry] = useState();
    const [newTemplateState, setNewTemplateState] = useState(null);
    const [newTemplateVersion, setNewTemplateVersion] = useState('');
    const loadBaseTemplate = () => {
        asyncDispatch(adminLoadTemplate(baseTemplateId))
            .then((template) => {
            setBaseTemplate(template);
            setBaseTemplateType(template.type);
            setBaseTemplateCountry(template.country);
            setBaseTemplateState(template.state);
            setBaseTemplateVersion(template.version);
            dispatch(setMessages({
                value: `The base template has been loaded`,
                severity: 'success'
            }));
        })
            .catch((error) => {
            dispatch(setMessages({
                value: 'Unable to load the base template',
                severity: 'error'
            }));
            console.error(error);
        });
    };
    const createTemplate = () => {
        if (!baseTemplate ||
            !newTemplateType ||
            !newTemplateCountry ||
            !newTemplateVersion) {
            dispatch(setMessages({
                value: `Please load a base template and fill in at least new template type, country, and version fields`,
                severity: 'error'
            }));
            return;
        }
        if (newTemplateType === baseTemplateType &&
            newTemplateCountry === baseTemplateCountry &&
            newTemplateState === baseTemplateState &&
            newTemplateVersion === baseTemplateVersion) {
            dispatch(setMessages({
                value: `The new template is the same as the base template`,
                severity: 'error'
            }));
            return;
        }
        asyncDispatch(adminCreateTemplateFromBase({
            type: newTemplateType,
            country: newTemplateCountry,
            state: newTemplateState === '' ? null : newTemplateState,
            version: newTemplateVersion,
            baseTemplateId
        }))
            .then(() => {
            if (baseTemplateInactive) {
                void asyncDispatch(adminUpdateTemplateConfiguration({
                    sid: baseTemplate.sid,
                    active: false
                }));
            }
            dispatch(setMessages({
                value: `The new template has been created`,
                severity: 'success'
            }));
        })
            .catch((error) => {
            dispatch(setMessages({
                value: 'Unable to create the new template',
                severity: 'error'
            }));
            console.error(error);
        });
    };
    const handleBaseTemplateId = useCallback((e) => {
        setBaseTemplateId(e.target.value);
    }, []);
    const handleNewTemplateType = useCallback((e) => {
        setNewTemplateType(e.target.value);
    }, []);
    const handleNewTemplateCountry = useCallback((e) => {
        setNewTemplateCountry(e.target.value);
    }, []);
    const handleNewTemplateState = useCallback((e) => {
        setNewTemplateState(e.target.value);
    }, []);
    const handleNewTemplateVersion = useCallback((e) => {
        setNewTemplateVersion(e.target.value);
    }, []);
    const handleDeactivateBaseTemplate = useCallback((_, checked) => {
        setBaseTemplateInactive(checked);
    }, []);
    const idTemplateTypeOptions = useMemo(() => idTemplateTypes.map((v) => ({ name: v, id: v })), []);
    const countryOptions = useMemo(() => {
        const out = [];
        for (const id of Object.values(Country)) {
            out.push({ id, name: `${countryNames[id]} (${id})` });
        }
        return out;
    }, []);
    return (React.createElement("div", null,
        React.createElement("h1", null, "Create Template From Base"),
        React.createElement("div", { className: "filters-container" },
            React.createElement("p", { style: { marginBottom: '20px' } }, "Create a new template based on the image and configuration of an already existing template."),
            React.createElement(Grid, { container: true },
                React.createElement(Grid, { item: true, xs: 3 },
                    React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", label: "Base Template ID", onChange: handleBaseTemplateId, required: true, value: baseTemplateId })),
                React.createElement(Grid, { item: true, xs: 3 },
                    React.createElement(Button, { disabled: !baseTemplateId, onClick: loadBaseTemplate }, "Load"))),
            React.createElement(Grid, { container: true, style: { marginTop: '20px' } },
                React.createElement(Grid, { container: true, item: true, spacing: 2, xs: 12 },
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", disabled: true, label: "Base Template Type", value: baseTemplateType })),
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", disabled: true, label: "Base Template Country", value: baseTemplateCountry })),
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", disabled: true, label: "Base Template State", value: baseTemplateState })),
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", disabled: true, label: "Base Template Version", value: baseTemplateVersion })))),
            React.createElement(Grid, { container: true, style: { marginTop: '20px' } },
                React.createElement(Grid, { container: true, item: true, spacing: 2, xs: 12 },
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(Select, { className: "g-full-width u-margin-bottom-small", isClearable: false, items: idTemplateTypeOptions, label: "New Template Type", onChange: handleNewTemplateType, required: true, value: newTemplateType })),
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(Select, { className: "g-full-width u-margin-bottom-small", isClearable: false, items: countryOptions, label: "New Template Country", onChange: handleNewTemplateCountry, required: true, value: newTemplateCountry })),
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", label: "New Template State", onChange: handleNewTemplateState, value: newTemplateState })),
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", label: "New Template Version", onChange: handleNewTemplateVersion, required: true, value: newTemplateVersion }))),
                React.createElement(Grid, { container: true, style: { marginTop: '20px' } },
                    React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: baseTemplateInactive, onChange: handleDeactivateBaseTemplate }), label: "Deactivate Base Template" }))),
            React.createElement(Grid, { container: true, style: { marginTop: '20px' } },
                React.createElement(Button, { onClick: createTemplate }, "Create")))));
};
